/**
 * Hook for managing signing field placements
 *
 * Features:
 * - Load field placements from GraphQL
 * - Add, update, delete field placements
 * - Auto-save with 500ms debounce
 * - Track selected field
 * - Optimistic updates with error handling
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { SaveFieldPlacementsMutation } from '../../../api/mutations';

export interface FieldPlacement {
  id: string;
  type: 'signature' | 'initials' | 'date' | 'text';
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  signer?: string;
  req: boolean;
  data?: Record<string, any>;
}

export interface UseFieldPlacementsOptions {
  documentId: string;
  initialFields?: FieldPlacement[];
  onSaveSuccess?: () => void;
  onSaveError?: (error: Error) => void;
}

export interface UseFieldPlacementsResult {
  fields: FieldPlacement[];
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  addField: (field: Omit<FieldPlacement, 'id'>) => void;
  updateField: (id: string, updates: Partial<FieldPlacement>) => void;
  deleteField: (id: string) => void;
  clearAll: () => void;
  loading: boolean;
  saving: boolean;
}

/**
 * Hook for managing field placements with auto-save
 */
export const useFieldPlacements = ({
  documentId,
  initialFields = [],
  onSaveSuccess,
  onSaveError,
}: UseFieldPlacementsOptions): UseFieldPlacementsResult => {
  const [fields, setFields] = useState<FieldPlacement[]>(initialFields);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Auto-save debounce timer
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fieldsRef = useRef<FieldPlacement[]>(fields);

  // Keep ref in sync with state
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  // Sync state with initialFields when they change (GraphQL query loads)
  // This ensures fields persist across navigations
  useEffect(() => {
    setFields(initialFields);
  }, [initialFields]);

  const [saveFieldPlacementsMutation] = useMutation(SaveFieldPlacementsMutation);

  /**
   * Save field placements to backend with debounce
   */
  const saveFieldPlacements = useCallback(async (fieldsToSave: FieldPlacement[]) => {
    try {
      setSaving(true);

      const result = await saveFieldPlacementsMutation({
        variables: {
          documentId,
          fieldPlacements: fieldsToSave.map(f => ({
            id: f.id,
            type: f.type,
            page: f.page,
            x: f.x,
            y: f.y,
            w: f.w,
            h: f.h,
            signer: f.signer,
            req: f.req,
            data: f.data || {},
          })),
        },
      });

      onSaveSuccess?.();
    } catch (error) {
      onSaveError?.(error as Error);
    } finally {
      setSaving(false);
    }
  }, [documentId, saveFieldPlacementsMutation, onSaveSuccess, onSaveError]);

  /**
   * Trigger auto-save with 500ms debounce
   */
  const triggerAutoSave = useCallback(() => {
    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Set new timer
    saveTimerRef.current = setTimeout(() => {
      saveFieldPlacements(fieldsRef.current);
    }, 500);
  }, [saveFieldPlacements]);

  /**
   * Add a new field
   */
  const addField = useCallback((field: Omit<FieldPlacement, 'id'>) => {
    const newField: FieldPlacement = {
      ...field,
      id: uuidv4(),
    };

    setFields(prev => [...prev, newField]);
    setSelectedId(newField.id);
    triggerAutoSave();
  }, [triggerAutoSave]);

  /**
   * Update an existing field
   */
  const updateField = useCallback((id: string, updates: Partial<FieldPlacement>) => {
    setFields(prev =>
      prev.map(field =>
        field.id === id ? { ...field, ...updates } : field
      )
    );
    triggerAutoSave();
  }, [triggerAutoSave]);

  /**
   * Delete a field
   */
  const deleteField = useCallback((id: string) => {
    setFields(prev => prev.filter(field => field.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
    }
    triggerAutoSave();
  }, [selectedId, triggerAutoSave]);

  /**
   * Clear all fields
   */
  const clearAll = useCallback(() => {
    setFields([]);
    setSelectedId(null);
    triggerAutoSave();
  }, [triggerAutoSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  return {
    fields,
    selectedId,
    setSelectedId,
    addField,
    updateField,
    deleteField,
    clearAll,
    loading: false, // Can be extended to track initial load
    saving,
  };
};
